import { UserRowData } from "../../types/general-types";

export type CourseTableEntry = {
	courseId: number;
	courseName: string;
	courseCode: string;
	studentCount: number;
	flaggedEvents: number;
	severityLevel: number;
};

export type AssessmentTableEntry = {
	quizId: number;
	quizName: string;
	studentCount: number;
	flaggedEvents: number;
	severityLevel: number;
};

export type StatsCardsData = {
	assessments?: { total: number; month: number };
	students?: { total: number; month: number };
	veritySessions?: { total: number; month: number };
	liveSessions?: { total: number; month: number };
	proctoringHours?: { total: number; month: number };
	liveHours?: { total: number; month: number };
	averageSessions?: { total: number; change: number };
	averageHours?: { total: number; change: number };
};

export type InstitutionReportEmailData = {
	institutionId: number;
	month: number;
	year: number;
	adminEmails: string[];
	topMetrics: TopMetric[];
	title: string;
	summary: string;
};

export type TopMetric =
	| {
			type: "increase";
			key: "totalFlags" | "totalAttempts" | "totalAssessments" | "totalHours" | "totalLiveHours" | "totalTestTakers";
			currentValue: string;
			previousValue: string;
			percentChange: string;
			texts: [string, string, string];
			imageUrl: string;
	  }
	| {
			type: "increase-csm";
			key: "totalFlags" | "totalAttempts" | "totalAssessments" | "totalHours" | "totalLiveHours" | "totalTestTakers";
			currentValue: string;
			previousValue: string;
			percentChange: string;
			texts: [string, string, string];
			imageUrl: string;
	  }
	| {
			type: "decrease-csm";
			key: "totalFlags" | "totalAttempts" | "totalAssessments" | "totalHours" | "totalLiveHours" | "totalTestTakers";
			currentValue: string;
			previousValue: string;
			percentChange: string;
			texts: [string, string, string];
			imageUrl: string;
	  }
	| {
			type: "absolute";
			key: "totalFlags" | "totalAttempts" | "totalAssessments" | "totalHours" | "totalLiveHours" | "totalTestTakers";
			currentValue: string;
			previousValue: string;
			percentChange: string;
			texts: [string, string, string];
	  }
	| {
			type: "absolute-csm";
			key: "totalFlags" | "totalAttempts" | "totalAssessments" | "totalHours" | "totalLiveHours" | "totalTestTakers";
			currentValue: string;
			previousValue: string;
			percentChange: string;
			texts: [string, string, string];
	  };

export type ReportResult = StatsCardsData & {
	monthlyReport?: {
		totalVeritySessions: number[];
		totalLiveSessions: number[];
		totalLiveCheckInSessions: number[];
		totalNonLiveSessions: number[];
	};
	severityCounts: {
		none: number;
		minor: number;
		major: number;
		severe: number;
	};
	platformCounts?: {
		noneCount: number;
		lockdownCount: number;
		mobileCount: number;
		extensionCount: number;
	};
	completionCount?: {
		complete: number;
		incomplete: number;
		unavailable: number;
		pending: number;
		error: number;
	};
	courseTableReport?: CourseTableEntry[];
	assessmentTableReport?: AssessmentTableEntry[];
	testTakerTableReport?: UserRowData[];
};

export type ExportReportInfo =
	| {
			reportType: Extract<ReportType, "institution" | "course">;
			institutionCourseReportInfo: {
				id?: number; // Course Report may have id undefined
				startDate: Date;
				endDate: Date;
				verityFetch: typeof fetch;
				institutionInfo?: { subAccountName?: string; subAccountId?: number };
				courseInfo?: { courseName: string };
			};
			assessmentReportInfo?: never;
	  }
	| {
			reportType: Extract<ReportType, "assessment">;
			assessmentReportInfo: ReportResult & { assessmentName: string };
			institutionCourseReportInfo?: never;
	  };

export type ExportReportEmail = {
	toEmail: string;
	reportType: ReportType;
	filters?: string;
	fileName: string;
	fileContent: string; // base64 encoded
	contentType: string;
};

export const FileExtensionMap: Record<FormatType, string> = {
	json: "json",
	excel: "xlsx",
	csv: "zip", // zip csv file
};

export type ExportResult = { success: true } | { success: false; error: string };

export type SubAccountNode = {
	id: number;
	accountName: string;
	lmsAccountId: string;
	parentLmsAccountId: string | null;
	rootLmsAccountId: string | null;
	children: SubAccountNode[];
};

export type ReportType = "institution" | "course" | "assessment";

export type ExportType = "download" | "email";
export type FormatType = "json" | "csv" | "excel";

export type ExportReportRadioGroupProps = {
	onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
	selectedValue: string;
	type: "export" | "format";
};

export type YearSelection = number | "current";

export interface CenterTextPluginOptions {
	display: boolean;
	semiCircle?: boolean;
	smallText?: boolean;
	text: string;
}
